import { convertLayoutItem, convertShortInfoEntry } from './common';
import { convertSimplifiedFacilityResponse } from './facility';
import { convertSimplifiedFloorResponse } from './floor';
import { convertSimplifiedProjectChildItemResponse } from './projectChildItem';
import {
  ProjectItemTypes,
  SimplifiedFloorTypes,
  SimplifiedProjectChildItemTypes,
  SimplifiedProjectItemTypes,
  SimplifiedFacilityTypes,
} from '../../types';
import {
  AmenitiesLayoutTypes,
  BuildingLayoutTypes,
  VillaBlockLayoutTypes,
} from '../../types/layout';
import {
  BlockLayoutResponseTypes,
  BuildingLayoutResponseTypes,
  ProjectItemResponseTypes,
  SimplifiedProjectItemResponseTypes,
  SimplifiedFacilityResponseTypes,
} from '../../types/response';
import { PROJECT_ITEM_TYPE } from '../../commons/constants';
import { checkValidUrl } from '../shareLinkUtils';

export const convertBuildingLayoutResponse = (
  item: BuildingLayoutResponseTypes
): BuildingLayoutTypes => {
  const layout = convertLayoutItem(item);
  return {
    ...layout,
    id: `${item.projectItemId}_${item.itemId}`,
    projectItemId: item.projectItemId,
    floorId: item.itemId,
  };
};

export const convertBlockLayoutResponse = (
  item: BlockLayoutResponseTypes
): VillaBlockLayoutTypes => {
  const layout = convertLayoutItem(item);
  return {
    ...layout,
    id: `${item.projectItemId}_${item.itemId}`,
    projectItemId: item.projectItemId,
    projectChildItemId: item.itemId,
  };
};

export const convertSimplifiedProjectItemResponse = (
  item: SimplifiedProjectItemResponseTypes
): SimplifiedProjectItemTypes => {
  const { shortInformation, introduction, information } = convertShortInfoEntry(item.shortInfo);

  let facilities: SimplifiedFacilityTypes[] = [];
  let groupFacilities: SimplifiedFacilityTypes[] = [];
  if (item.facilities && !!item.facilities.length) {
    facilities = item.facilities
      .filter((i) => !i.numberOfFacilities)
      .map((i) => convertSimplifiedFacilityResponse(i));

    groupFacilities = item.facilities
      .filter((i) => i.numberOfFacilities)
      .map((i) => convertSimplifiedFacilityResponse(i))
      .sort((a, b) => b.numberOfFacilities - a.numberOfFacilities);
  }

  return {
    id: item.id,
    code: item.code,
    type: item.type,
    name: item.name,
    description: item.description,
    numberOfApartments: item.numberOfApartments,
    numberOfFloors: item.numberOfFloors,
    numberOfVillas: item.numberOfVillas,
    image: item.avatar,
    shortInformation,
    introduction,
    information,
    alias: item.alias,
    facilities,
    facilityCodes: facilities.map((i) => i.code),
    groupFacilities,
    groupFacilityCodes: groupFacilities.map((i) => i.code),
    rating: item.rating,
    ratingDescription: item.ratingDescription,
    isSimplified: true,
    shortDescription: item.shortDescription,
    floorDefault: item.floorDefault,
    address: item.address,
    display: item.display,
    orderIndex: item.orderIndex,
    icon: item.icon,
    virtualTourUrl: checkValidUrl(item.virtualTourUrl) ? item.virtualTourUrl : undefined,
  };
};

export const convertFacilityToLayoutItem = (
  projectItem: SimplifiedProjectItemResponseTypes,
  item: SimplifiedFacilityResponseTypes
): AmenitiesLayoutTypes => {
  return {
    id: `${projectItem.id}_${item.id}`,
    projectItemId: projectItem.id,
    zoneCoords: item.zoneCoords,
    zoneColor: item.zoneColor,
    labelCoords: item.labelCoords,
    labelColor: item.labelColor,
    transparent: item.transparent,
    labelTransparent: item.labelTransparent,
    code: item.code,
  };
};

export const convertProjectItemResponse = (item: ProjectItemResponseTypes): ProjectItemTypes => {
  const projectItem = convertSimplifiedProjectItemResponse(item);
  const { layoutItems, floors = [], villas = [], type } = item;

  let items:
    | SimplifiedFloorTypes[]
    | SimplifiedProjectChildItemTypes[]
    | SimplifiedFacilityTypes[] = [];
  let layouts: VillaBlockLayoutTypes[] | BuildingLayoutTypes[] | AmenitiesLayoutTypes[] = [];

  let layoutImage = item.imageLayout;

  if (type === PROJECT_ITEM_TYPE.BUILDING) {
    items = (floors || []).map((i) => convertSimplifiedFloorResponse(i));
    layouts = ((layoutItems as BuildingLayoutResponseTypes[]) || []).map((i) => {
      return convertBuildingLayoutResponse(i);
    });
  } else if (type === PROJECT_ITEM_TYPE.VILLA_BLOCK) {
    items = (villas || []).map((i) => convertSimplifiedProjectChildItemResponse(i));
    layouts = (layoutItems || []).map((i) => convertBlockLayoutResponse(i));
  } else if (type === PROJECT_ITEM_TYPE.AMENITY) {
    items = [...projectItem.facilities, ...projectItem.groupFacilities];
    layouts = [...projectItem.facilities, ...projectItem.groupFacilities].map((i) => {
      return convertFacilityToLayoutItem(item, i);
    });
    layoutImage = item.facilitiesImageLayout;
  }

  return {
    ...projectItem,
    overlayLayout: item.overlayLayout,
    visualizeCode: item.visualizeCode,
    media: item.gallery,
    locationId: item.location && item.location.id,
    itemIds: (items || []).map((i: SimplifiedFloorTypes | SimplifiedProjectChildItemTypes) => i.id),
    items,
    layoutImage,
    layoutItems: layouts,
    isSimplified: false,
    isDisabled: !items.length,
    orderIndex: item.orderIndex,
    layout360Items: item.layout360Items,
    layout360: item.layout360,
  };
};
